/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @nx/enforce-module-boundaries */
import {
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { LookupOptionsService } from '../../../services/lookup-options.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TextInputComponent } from '../text-input/text-input.component';
import { InputStatesService } from '../../../services/input-states.service';
import { injectVirtualizer } from '@tanstack/angular-virtual';
import { SkeletonLoaderComponent } from '../../skeleton-loader/skeleton-loader.component';
import { debounce, EMPTY, ReplaySubject, timer } from 'rxjs';
import { RadioButtonComponent } from '../radio-button/radio-button.component';
import { CommonModule } from '@angular/common';

export interface Option {
  title: string;
  value: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-text-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TextInputComponent,
    LoadingSpinnerComponent,
    RadioButtonComponent,
    SkeletonLoaderComponent,
  ],
  templateUrl: './text-search.component.html',
  styleUrl: './text-search.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TextSearchComponent),
    },
  ],
})
export class TextSearchComponent implements ControlValueAccessor, OnInit {
  scrollElement = viewChild<ElementRef<HTMLDivElement>>('scrollElement');

  readonly placeHolder = input('');
  readonly label = input<string | undefined>();
  readonly name = input.required<string>();

  options = input<Option[]>([]);
  loading = input(false);
  loadingData = output<boolean>();
  emptyResponse = output();
  showOptions = signal(false);
  guid = input<string>('');
  showSelectedFault = input<boolean>(false);
  showResultCount = input<boolean>(false);
  textChanged = output<string>();
  exampleText = input<string>('');

  searchTerm = '';
  value: string | undefined;
  touched = false;
  disabled = false;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  searchChanged = new ReplaySubject(1);
  private readonly viewModel = computed(() => {
    return {
      label: this.label(),
      name: this.name(),
      placeHolder: this.placeHolder(),
      loading: this.loading(),
      options: this.options(),
      showOptions: this.showOptions(),
    };
  });

  get vm() {
    return this.viewModel();
  }

  virtualiser = injectVirtualizer(() => ({
    scrollElement: this.scrollElement(),
    count: this.options().length,
    estimateSize: () => 71,
    overscan: 5,
  }));

  constructor(
    private readonly inputStateService: InputStatesService,
    private readonly lookupOptions: LookupOptionsService
  ) {}

  onOptionClick(option: Option) {
    this.value = option.value;
    this.onChange(option);
    this.showOptions.set(false);
    this.searchTerm = option.title;
  }

  ngOnInit() {
    this.searchTerm = this.inputStateService.getStoredSearchTerm(this.vm.name);
    this.loadDataFromSearch();
  }

  writeValue(value: any): void {
    this.value = value;
    this.onChange(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  loadDataFromSearch(): void {
    // Use a debounce to prevent spam of the endpoints
    this.searchChanged
      .asObservable()
      .pipe(debounce(() => timer(200)))
      .subscribe({
        next: () => {
          this.textChanged.emit(this.searchTerm);
        },
      });
  }

  onSearchChange(event: string) {
    this.searchTerm = event || '';
    this.inputStateService.storeSearchTerm(this.vm.name, this.searchTerm);
    if (event === '') return;

    this.loadingData.emit(true);
    this.searchChanged.next(EMPTY);
  }
}
